/**
 * One account menu, in both apps — `docs/identity-and-sign-in.md` §1, board 1.
 *
 * It was `UserMenu` inside `header.tsx`, reachable only from the office frame,
 * and Rowted Admin had a different menu of its own. Board 1's argument is that
 * they are the same menu: the same head, the same Appearance control, the same
 * **Account** section, and rows that differ only where the app does. So this
 * takes the app's rows as data and knows nothing about either side.
 *
 * **Environments appear only when there is more than one.** Somebody who works
 * at one company and nowhere else should not be told about a feature that
 * cannot do anything for them — and the list is found by address, so "more
 * than one" is a fact about the person, not a setting.
 *
 * Plan §3.4: a row the role cannot use is removed, not greyed. The caller
 * decides that by what it puts in `rows`.
 */
import { type ReactNode } from 'react'
import { Popover } from '../components/overlays.js'
import { Avatar, Segmented, Tag, type TagTone } from '../components/primitives.js'
import {
  IconBell, IconChevronRight, IconCheck, IconMoon, IconSignOut, IconUserRound,
} from './icons.js'
import { cn } from '../lib/cn.js'
import type { Theme } from '../lib/theme.js'

/** One place this person can sign in to, as the server describes it (§2.1). */
export interface MenuEnvironment {
  kind: 'tenant' | 'platform'
  tenantId?: string
  name: string
  host: string
  role: string
  email: string
  linked: boolean
}

/** A row the app itself contributes — People, Catalog, Health. */
export interface MenuRowSpec {
  label: string
  sub?: string
  tag?: string
  icon: (props: { size?: number; className?: string }) => JSX.Element
  onClick: () => void
}

export interface AccountMenuProps {
  open: boolean
  onClose: () => void
  anchor: HTMLElement | null
  who: {
    name: string
    email: string
    /** "Owner · Woolley · all branches" — one line, built by the caller. */
    line: string
  }
  theme: Theme
  onThemeChange: (theme: Theme) => void
  /** '/account' or '/admin/account'. */
  profileHref: string
  onGo: (path: string) => void
  /**
   * Always drawn (§1's order), not only on a phone.
   *
   * The bell in the bar is a shortcut; this row is the labelled way in and the
   * one that says how many are waiting without hovering anything. Two controls
   * for one thing is the same bargain search already makes with ⌘K.
   */
  notifications?: { count: number; onOpen: () => void }
  environments: MenuEnvironment[]
  /** Which row wears the check. */
  current: { kind: 'tenant' | 'platform'; tenantId?: string }
  onSwitch: (target: MenuEnvironment) => void
  rows: MenuRowSpec[]
  onSignOut: () => void
}

const isCurrent = (
  one: MenuEnvironment, current: AccountMenuProps['current'],
): boolean => one.kind === current.kind
  && (one.kind === 'platform' || one.tenantId === current.tenantId)

export function AccountMenu({
  open, onClose, anchor, who, theme, onThemeChange, profileHref, onGo,
  notifications, environments, current, onSwitch, rows, onSignOut,
}: AccountMenuProps) {
  const go = (path: string) => () => { onClose(); onGo(path) }

  return (
    <Popover open={open} onClose={onClose} anchor={anchor} align="right" width={320}
      className="rounded-menu" label="Your account">
      {/* --- who you are -------------------------------------------------
        * One muted line under the address, not two chips. Board 1 says
        * "Owner · Woolley · all branches" in one breath; two tags made the
        * role and the ground look like two controls.
        */}
      <div className="flex items-center gap-3 px-[18px] pb-3 pt-3.5">
        <Avatar name={who.name} self size={36} />
        <div className="flex min-w-0 flex-col gap-0.5">
          <b className="truncate text-[15px] font-bold text-text">{who.name}</b>
          <span className="truncate text-[12.5px] text-text-muted">{who.email}</span>
          <span className="truncate text-[12px] text-text-faint">{who.line}</span>
        </div>
      </div>

      {/*
        * Everything between the head and Sign out scrolls — item 5, Aaron:
        * "the user menu drop down on smaller screens gets cut off at the
        * bottom and doesn't scroll if it's too long."
        *
        * `Popover` already caps its own height against the window and clips
        * what does not fit, so the menu was not broken so much as silent:
        * with six environments and four app rows on a 600px laptop, Sign out
        * was simply below the edge with no way to reach it. The cap is right;
        * what was missing is something inside willing to scroll.
        *
        * The head and Sign out stay put. Sign out is the row somebody opens
        * this menu for when something has gone wrong, and a row you have to
        * scroll to find is a row that is not there.
        */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">

      {/*
        * **The bell, and only where there is no bell** (Aaron).
        *
        * This row and the `Notification settings` row that used to sit under
        * My profile were both removed from the menu: the bell in the header
        * opens the panel and the profile holds the settings, so on a desktop
        * each was a second door to a room with one already. The row survives
        * on a phone because there the header has no bell at all — it folds in
        * here to give the search box its 36px back — and a menu that drops it
        * everywhere would leave a phone with no way to notifications.
        *
        * It is the *caller* that decides, by passing `notifications` only at
        * that width, so there is one rule: exactly one way to the bell on
        * every screen.
        */}
      {notifications && (
        <MenuRow
          label="Notifications"
          icon={IconBell}
          tag={notifications.count > 0 ? String(notifications.count) : undefined}
          tagTone="bad"
          onClick={() => { onClose(); notifications.onOpen() }}
        />
      )}

      {/* --- appearance ---------------------------------------------------
        * Here rather than on My profile: it is per device (plan §3.4), and
        * the device you want to change it on is the one you are looking at.
        */}
      <div className="flex items-center gap-3 px-5 pb-3 pt-1.5">
        <span aria-hidden className="flex w-5 justify-center text-text-body">
          <IconMoon size={18} />
        </span>
        <span className="text-[14px] font-semibold text-text">Dark mode</span>
        <span className="ml-auto">
          <Segmented
            label="Dark mode"
            size="large"
            value={theme}
            onChange={onThemeChange}
            options={[
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' },
              { value: 'auto', label: 'Auto' },
            ]}
          />
        </span>
      </div>

      <Divider />

      <SectionLabel>Account</SectionLabel>
      <MenuRow
        label="My profile"
        icon={IconUserRound}
        sub="password · passkeys · two-step · devices"
        onClick={go(profileHref)}
      />
      {environments.length > 1 && (
        <>
          <Divider />
          <SectionLabel>Environments</SectionLabel>
          {environments.map((one) => (
            <EnvironmentRow
              key={`${one.kind}:${one.tenantId ?? 'platform'}`}
              environment={one}
              here={isCurrent(one, current)}
              onPick={() => { onClose(); onSwitch(one) }}
            />
          ))}
        </>
      )}

      <Divider />

      {rows.map((row) => (
        <MenuRow
          key={row.label}
          label={row.label}
          icon={row.icon}
          sub={row.sub}
          tag={row.tag}
          onClick={() => { onClose(); row.onClick() }}
        />
      ))}

      </div>

      <div className="shrink-0 border-t border-line-soft pb-1.5 pt-1.5">
        <MenuRow label="Sign out" icon={IconSignOut} danger onClick={onSignOut} />
      </div>
    </Popover>
  )
}

/** The 10.5px uppercase heading over a group (§3.2). */
function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-5 pb-1 pt-2 text-fieldlabel font-semibold uppercase
                    tracking-[0.05em] text-text-faint">
      {children}
    </div>
  )
}

/**
 * One environment.
 *
 * The mark, then the name, then `host · role` — or `host · other-address ·
 * linked` when this row belongs to an address the person linked rather than
 * the one they are signed in with. Saying whose address it is matters:
 * switching there signs them in as somebody else's address, and a row that
 * hid that would be a surprise at the other end.
 */
function EnvironmentRow({ environment, here, onPick }: {
  environment: MenuEnvironment
  here: boolean
  onPick: () => void
}) {
  const sub = environment.linked
    ? `${environment.host} · ${environment.email} · linked`
    : `${environment.host} · ${environment.role}`

  return (
    <button
      type="button"
      onClick={here ? undefined : onPick}
      aria-current={here ? 'true' : undefined}
      className={cn('mx-1.5 flex w-[calc(100%-12px)] items-center gap-3 rounded-[10px]',
        'px-3.5 py-[9px] text-left',
        here ? 'bg-accent-soft' : 'hover:bg-hover')}
    >
      <EnvironmentMark environment={environment} />
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-[14px] font-semibold text-text">
            {environment.name}
          </span>
          {environment.kind === 'platform' && <Tag tone="dark">Admin</Tag>}
        </span>
        <span className="truncate text-[12px] text-text-muted">{sub}</span>
      </span>
      <span aria-hidden className={cn('flex shrink-0',
        here ? 'text-accent-dark' : 'text-text-faint')}>
        {here ? <IconCheck size={15} /> : <IconChevronRight size={14} />}
      </span>
    </button>
  )
}

/**
 * 26px: a company's initials on its own tint, Rowted Admin's black shield.
 *
 * Initials rather than a logo because the menu lists places this person can
 * *go*, and fetching another company's mark from here would mean this
 * customer's page asking for that customer's file.
 */
function EnvironmentMark({ environment }: { environment: MenuEnvironment }) {
  if (environment.kind === 'platform') {
    return (
      <span aria-hidden className="flex size-[26px] shrink-0 items-center justify-center
                                   rounded-[7px] bg-text text-white">
        <IconShieldMark />
      </span>
    )
  }
  const initials = environment.name.split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]!.toUpperCase()).join('')
  return (
    <span aria-hidden className="flex size-[26px] shrink-0 items-center justify-center
                                 rounded-[7px] bg-accent-soft text-[10.5px] font-bold
                                 text-accent-dark">
      {initials}
    </span>
  )
}

function IconShieldMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  )
}

/** The hairline between groups — inset, so it does not touch the menu's edge. */
function Divider() {
  return <div aria-hidden className="mx-3 my-1.5 h-px bg-line-soft" />
}

/**
 * A row of the account menu.
 *
 * Icon in a 20px well so every label starts level, name at 14/600, the line
 * under it saying what is behind the row — "Settings" and "Catalog" are not
 * self-explanatory to somebody who has not opened them — and a chevron at the
 * right, because a row that goes somewhere should look like it does.
 */
export function MenuRow({ label, tag, tagTone, icon: Icon, sub, danger, onClick }: {
  label: string
  tag?: string
  /** Board 1 draws the unread count red; `admin` and `1 degraded` stay plain. */
  tagTone?: TagTone
  icon: (props: { size?: number; className?: string }) => JSX.Element
  sub?: string
  /** Sign out, and nothing else. */
  danger?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn('mx-1.5 flex items-center gap-3 rounded-[10px] px-3.5 py-[9px]',
        'text-left hover:bg-hover',
        // `w-auto` rather than `w-full`: the 6px side margin is the hover
        // shape, and a full-width button would draw it to the menu's edge.
        'w-[calc(100%-12px)]')}
    >
      <span aria-hidden className={cn('flex w-5 shrink-0 justify-center',
        danger ? 'text-danger' : 'text-text-body')}>
        <Icon size={18} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-px">
        <span className={cn('truncate text-[14px] font-semibold',
          danger ? 'text-danger' : 'text-text')}>
          {label}
        </span>
        {sub && <span className="truncate text-[12px] text-text-muted">{sub}</span>}
      </span>
      {tag
        ? <Tag tone={tagTone} className="shrink-0">{tag}</Tag>
        : !danger && (
          <span aria-hidden className="flex shrink-0 text-text-faint">
            <IconChevronRight size={14} />
          </span>
        )}
    </button>
  )
}
